import axios from "../../utils/axios";
import { useAuth } from "../../store/auth";

// Resolves whichever local patientId a booking flow should actually use, given a patient-finder
// selection that may belong to a different (connected) tenant than the one being booked into.
// Unifies both DWA cross-tenant booking directions into one function -- whichever of
// patientHomeTenantId/targetTenantId differs from the current tenant gets the
// transactionalApiTenantId axios option; the other is a plain local call:
//   - GP receptionist booking into Hub: patientHomeTenantId omitted (defaults to current/GP),
//     targetTenantId = the delegate Hub -- local GET, cross-tenant POST.
//   - Hub booking on its own diary, patient picked from a connected GP's patient-finder results:
//     patientHomeTenantId = that GP tenant, targetTenantId omitted (defaults to current/Hub) --
//     cross-tenant GET, local POST.
// A same-tenant selection (the overwhelmingly common case) short-circuits before either call.
export async function resolveDelegatePatientId({
  patientId,
  patientHomeTenantId,
  targetTenantId,
}: {
  patientId?: string;
  patientHomeTenantId?: string;
  targetTenantId?: string;
} = {}) {
  const currentTenant = useAuth().selectedTenant;
  const homeTenant = patientHomeTenantId ?? currentTenant;
  const target = targetTenantId ?? currentTenant;

  if (homeTenant === target) {
    return patientId;
  }

  const { data: sourceData } = await axios.get(
    `/patient-admin/external-patient/data/get-patient-details-for-delegate/${patientId}`,
    homeTenant === currentTenant ? {} : { transactionalApiTenantId: homeTenant ?? undefined },
  );

  const { data } = await axios.post(
    "/patient-admin/external-patient/create-external-patient-for-delegate",
    {
      originTenantId: homeTenant,
      originPatientId: patientId,
      givenName: sourceData.givenName,
      familyName: sourceData.familyName,
      dateOfBirth: sourceData.dateOfBirth,
      gender: sourceData.gender,
      nhsNumber: sourceData.nhsNumber,
      emailAddress: sourceData.emailAddress,
      telephoneNumber: sourceData.telephoneNumber,
      address: sourceData.address,
    },
    target === currentTenant ? {} : { transactionalApiTenantId: target ?? undefined },
  );

  return data.patientId;
}
